import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import TextareaAutosize from "react-autosize-textarea";
import MaskedInput from "react-text-mask";
import PropTypes from "prop-types";

import TYPE from "constants/TextFieldType";
import MASK from "constants/mask";
import maskSSN from "utils/maskSSN";

import {
    BorderWidths,
    Colors,
    LineHeights,
    Radii,
    Spacing
} from "@paclife/plds.tokens";
import { Icon } from "@paclife/plds.icon";
import Typography from "common/Typography/Typography";

export const Wrapper = styled.div`
    width: 100%;
    /**  Icons are positioned as absolute */
    position: relative;
`;

export const Label = styled.label`
    display: block;
    margin-bottom: ${Spacing[4]};
    white-space: nowrap;
`;

export const LabelText = styled(Typography)`
    color: ${Colors.neutral600};
`;

export const InputStyle = css`
    display: block;
    width: 100%;
    height: 48px;
    border: ${BorderWidths.regular} solid ${Colors.neutral200};
    border-radius: ${Radii.rounded};
    outline: none;

    padding: 0 ${Spacing[16]};
    color: ${Colors.neutral1000};
    white-space: nowrap;
    text-overflow: ellipsis;

    :hover,
    :focus {
        border-color: ${Colors.blue500};
    }

    :disabled {
        background-color: #ffffff;
        color: ${Colors.disabled};
        :hover {
            border-color: ${Colors.neutral200};
        }
    }

    ::placeholder {
        color: ${Colors.neutral300};
    }

    ${({ error }) =>
        error === "true" &&
        css`
            border-color: ${Colors.destructive};
        `}
`;

const Input = styled.input`
    ${InputStyle}
`;

const TextArea = styled(TextareaAutosize)`
    ${InputStyle}
    /** Override styles for textarea */
    min-height: 144px;
    padding: 12px ${Spacing[16]}; // no token for 12px
    resize: none;
    line-height: ${LineHeights[200]};
    white-space: normal;
    ${({ overlimit }) =>
        overlimit === "true" &&
        css`
            border-color: ${Colors.destructive};
        `}
`;

const StyledMaskedInput = styled(MaskedInput)`
    ${InputStyle}
`;

export const HelperText = styled(Typography)`
    display: block;
    color: ${Colors.neutral600};
    margin-top: ${Spacing[8]};
`;

export const ErrorMessage = styled.div`
    margin-top: ${Spacing[8]};
    display: flex;
    align-items: center;
    span {
        color: ${Colors.destructive};
        margin-left: ${Spacing[4]};
    }
`;

export const IconWrapper = styled.span`
    position: absolute;
    top: 39px; // (label)24px + (half of input)24px - (icon)9px
    right: 16px;
    cursor: pointer;
`;

const CharacterCount = styled(Typography)`
    display: block;
    text-align: right;
    color: ${Colors.neutral600};
    margin-top: ${Spacing[8]};
`;

const TextField = ({
    className,
    type: typeProp,
    id,
    label,
    name,
    value,
    placeholder,
    instruction,
    errorMessage: errorMessageProp,
    error: errorProp,
    valid: validProp,
    allowpaste,
    onChange,
    onBlur,
    onFocus,
    onPaste,
    disabled,
    paragraph,
    maxChar,
    maskedInputProps,
    ...inputProps // the rest props will be passed into input element
}) => {
    const [data, setData] = useState(value);
    const [type, setType] = useState(typeProp);
    const [valid, setValid] = useState(validProp);
    const [error, setError] = useState(errorProp);
   // const [paste, setPaste] = useState(allowpaste);
    const [errorMessage, setErrorMessage] = useState(errorMessageProp);
    const [overCharLimit, setOverCharLimit] = useState(false);

    /** Re-render when prop is updated */
    useEffect(() => {
        setValid(validProp);
    }, [validProp]);
    useEffect(() => {
        setError(errorProp);
    }, [errorProp]);
    useEffect(() => {
        setErrorMessage(errorMessageProp);
    }, [errorMessageProp]);
    useEffect(() => {
        setData(value);
    }, [value]);

    /** Determine the returned component (default input, TextareaAutosize or MaskedInput) */
    let Component = Input;
    let textareaProps = {};

    if (paragraph) {
        Component = TextArea;
        textareaProps = { async: true };
    }
    if (type === TYPE.ssn) {
        Component = StyledMaskedInput;
        maskedInputProps = {
            mask: MASK.ssn,
            guide: false
        };
    }
    if (maskedInputProps.mask) {
        Component = StyledMaskedInput;
    }

    const handleChange = (e) => {
        setData(e.target.value);
        onChange(e);
        /** If the component is textarea, also check character count */
        if (paragraph && maxChar) {
            const charCount = e.target.value.length;
            if (charCount > maxChar) {
                setOverCharLimit(true);
            } else {
                setOverCharLimit(false);
            }
        }
    };

    const handlePaste = (e) => {
        onPaste(e);
        if (!allowpaste) {
            e.preventDefault();
        }
    };

    const handleBlur = (e) => {
        onBlur(e);
        if (type === TYPE.ssn) {
            setData((v) => maskSSN(v));
        }
    };

    const handleFocus = (e) => {
        onFocus(e);
        if (type === TYPE.ssn) {
            setData(value);
        }
    };

    /** Click to show/hide password */
    const wasPasswordType = typeProp === TYPE.password;
    const showPassword = () => {
        setType(TYPE.text);
    };
    const hidePassword = () => {
        setType(TYPE.password);
    };

    const TogglePasswordButton = () => {
        if (type === TYPE.password) {
            return (
                <IconWrapper
                    role="button"
                    onClick={showPassword}
                    data-testid="eye-show"
                >
                    <Icon name="show" width="16" color={Colors.neutral200} />
                </IconWrapper>
            );
        }
        if (type === TYPE.text && wasPasswordType) {
            return (
                <IconWrapper
                    role="button"
                    onClick={hidePassword}
                    data-testid="eye-hide"
                >
                    <Icon name="hide" width="16" color={Colors.neutral200} />
                </IconWrapper>
            );
        }
        return null;
    };

    /** Conditionally render success check icon. */
    const ToggleSuccessCheck = () => {
        // Hide when disabled, for password input and no data
        if (disabled || wasPasswordType || !data) {
            return null;
        }
        // Hide when valid is "false" or "undefined"
        if (!valid) {
            return null;
        }
        // Hide when there is error
        if (error) {
            return null;
        }
        return (
            <IconWrapper>
                <Icon
                    name="checkmarkOutline"
                    width={16}
                    color={Colors.success}
                    data-testid="success-check"
                />
            </IconWrapper>
        );
    };

    /** Render helper text OR error message.
     *  When there is error, display error message first,
     *  if no error message is specified, display helper text.
     */
    const DisplayInstruction = () => {
        if (!error && instruction) {
            return <HelperText variant="disclosure">{instruction}</HelperText>;
        }
        if (error && (errorMessage || instruction)) {
            return (
                <ErrorMessage>
                    <Typography variant="disclosure">
                        <strong>Error:</strong> {errorMessage || instruction}
                    </Typography>
                </ErrorMessage>
            );
        }
        return null;
    };

    const DisplayCharacterCount = () => {
        if (paragraph && maxChar) {
            return (
                <CharacterCount variant="caption">
                    {data.length === 0
                        ? `${maxChar} Characters`
                        : `${data.length}/${maxChar}`}
                </CharacterCount>
            );
        }
        return null;
    };

    return (
        <Wrapper className={className}>
            {label && (
                <Label htmlFor={id}>
                    <LabelText variant="microcopy">{label}</LabelText>
                </Label>
            )}
            <Component
                type={type}
                id={id}
                name={name}
                placeholder={placeholder}
                value={data}
                onPaste={handlePaste}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                error={String(error)}
                disabled={disabled}
                overlimit={String(overCharLimit)}
                {...textareaProps}
                {...maskedInputProps}
                {...inputProps}
            />
            <TogglePasswordButton />
            <ToggleSuccessCheck />
            <DisplayInstruction />
            <DisplayCharacterCount />
        </Wrapper>
    );
};

export default TextField;

TextField.propTypes = {
    /** Extend the styles applied to the component. */
    className: PropTypes.string,
    /** Determines the type of input field.
     * 'ssn' is a special custom type, to be handled differently. */
    type: PropTypes.oneOf([TYPE.text, TYPE.password, TYPE.ssn]),
    /** The id of the input element. */
    id: PropTypes.string,
    /** The label content. If specified, a label text is displayed. */
    label: PropTypes.string,
    /** The name attribute of the input element. */
    name: PropTypes.string,
    /** The value of the input element. */
    value: PropTypes.string,
    /** The placeholder content of the input element. */
    placeholder: PropTypes.string,
    /** The help text content. */
    instruction: PropTypes.string,
    /** The error message content. */
    errorMessage: PropTypes.string,
    /** Default as "false". If true, error state styling is applied.
     *  error props determines the value of state "valid",
     *  which in turn determines whether or not to show success check.
     */
    error: PropTypes.bool,
    /** Default as "undefined". If true, and error is false, show success check.
     */
    valid: PropTypes.bool,
    /* allowPaste string */
    allowpaste: PropTypes.bool,
    /** Change event handler. */
    onChange: PropTypes.func,
    /** Blur event handler. */
    onBlur: PropTypes.func,
    /** Focus event handler. */
    onFocus: PropTypes.func,
    /** Paste event handler */
    onPaste: PropTypes.func,
    /** If true, the component will render as TextArea. */
    paragraph: PropTypes.bool,
    /** Maximum number of characters allowed in the TextArea. */
    maxChar: PropTypes.number,
    /** Specify the disabled state of the input element. */
    disabled: PropTypes.bool,
    /** If specified, the component will render as MaskedInput.
     *  Refer to the official documentation for usage of the props.
     *  https://github.com/text-mask/text-mask/blob/master/componentDocumentation.md#readme */
    maskedInputProps: PropTypes.shape({
        mask: PropTypes.oneOfType([
            PropTypes.arrayOf(
                PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.instanceOf(RegExp)
                ])
            ),
            PropTypes.func
        ]),
        guide: PropTypes.bool,
        placeholderChar: PropTypes.string,
        keepCharPositions: PropTypes.bool,
        pipe: PropTypes.func,
        showMask: PropTypes.bool
    })
};

TextField.defaultProps = {
    className: "",
    type: TYPE.text,
    id: undefined,
    label: undefined,
    name: undefined,
    value: "",
    placeholder: undefined,
    instruction: undefined,
    errorMessage: undefined,
    error: false,
    allowpaste: true,
    valid: undefined,
    onPaste: () => {},
    onChange: () => {},
    onBlur: () => {},
    onFocus: () => {},
    paragraph: false,
    maxChar: undefined,
    disabled: false,
    maskedInputProps: {}
};